import { createContext, useContext } from 'react';

export type ThemeMode = 'light' | 'dark' | 'auto';

export const THEME_MODE_CONFIG = {
  STORAGE_KEY: 'scalar-app-theme-mode',
  DEFAULT: 'light' as ThemeMode,
} as const;

export interface ThemeModeContextType {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  /** The resolved effective mode ('light' or 'dark'), accounting for 'auto' + OS preference */
  resolvedMode: 'light' | 'dark';
}

const ThemeModeContext = createContext<ThemeModeContextType | undefined>(undefined);

export const useThemeMode = (): ThemeModeContextType => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
};

export const loadThemeModeFromStorage = (): ThemeMode => {
  try {
    const saved = localStorage.getItem(THEME_MODE_CONFIG.STORAGE_KEY);
    if (saved === 'light' || saved === 'dark' || saved === 'auto') {
      return saved;
    }
  } catch {
    // ignore
  }
  return THEME_MODE_CONFIG.DEFAULT;
};

export const saveThemeModeToStorage = (mode: ThemeMode): void => {
  try {
    localStorage.setItem(THEME_MODE_CONFIG.STORAGE_KEY, mode);
  } catch {
    // ignore
  }
};

export { ThemeModeContext };
